import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import PublicLayout from "@/components/PublicLayout";
import Seo from "@/components/Seo";
import { api, formatError } from "@/lib/api";
import { Loader2, CheckCircle2, ArrowRight, Tag } from "lucide-react";

export default function ContractorPartner() {
  const [form, setForm] = useState({
    business_name: "", contact_person: "", phone: "", email: "", abn: "",
    service_areas: "", services_offered: "", insurance_status: "", waste_disposal_capability: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contractors", form);
      setDone(true);
      toast.success("Application received");
    } catch (err) {
      toast.error(formatError(err));
    } finally { setSubmitting(false); }
  };

  return (
    <PublicLayout>
      <Seo title="Partner With Us | Site Clean-Up Contractors | Site Cleanup" description="Apply to join the Site Cleanup contractor network. Receive site clean-up quote opportunities from builders, developers and land owners." />
      <section className="bg-safety text-white py-20">
        <div className="rs-container">
          <span className="rs-eyebrow text-white/90">For contractors</span>
          <h1 className="mt-3 text-5xl md:text-7xl uppercase max-w-3xl">Join our contractor network.</h1>
          <p className="mt-4 max-w-2xl text-white/90 text-lg">We match Australian site clean-up contractors with real quote opportunities from builders, developers and land owners. Apply below to join for free, or head straight to pricing if you want a paid plan.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#apply" className="inline-flex items-center gap-2 bg-asphalt text-white px-6 py-3 font-semibold uppercase tracking-widest border-2 border-asphalt hover:bg-white hover:text-asphalt transition-colors" data-testid="partner-cta-apply">
              Apply free <ArrowRight size={16} />
            </a>
            <Link to="/pricing" className="rs-btn-outline-light" data-testid="partner-cta-pricing">
              See contractor pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Contractor pricing preview */}
      <section className="py-16 bg-white border-b border-border" data-testid="partner-pricing-preview">
        <div className="rs-container">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
            <div>
              <span className="rs-eyebrow"><Tag size={12} className="inline mr-1" /> Contractor pricing at a glance</span>
              <h2 className="mt-2 text-3xl md:text-4xl uppercase">Free to apply. Paid plans if you want volume.</h2>
              <p className="mt-2 text-muted-foreground max-w-2xl">Every contractor joins on the free tier. Upgrade any time — no lock-in.</p>
            </div>
            <Link to="/pricing" className="text-sm font-semibold text-safety hover:underline underline-offset-4">See full pricing →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {[
              { name: "Free", price: "A$0", tag: "forever", body: "Apply to the network, receive occasional matched leads." },
              { name: "Pro", price: "A$249", tag: "per 30 days · incl. GST", body: "Priority lead matching + unlimited contact reveals in your suburbs." },
              { name: "Premium", price: "A$499", tag: "per 30 days · incl. GST", body: "First-look on new leads + included in featured rotation." },
              { name: "Featured suburb", price: "A$299", tag: "per suburb / 30 days · incl. GST", body: "Be the featured contractor for a specific suburb." },
            ].map((t, i) => (
              <div key={t.name} className={`p-5 border ${i === 1 ? "border-safety ring-2 ring-safety/10" : "border-border"} bg-white`}>
                <p className="font-display uppercase text-lg">{t.name}</p>
                <p className="mt-2 font-display text-3xl">{t.price}<span className="text-xs text-muted-foreground font-sans font-normal ml-1">{t.tag}</span></p>
                <p className="mt-3 text-xs text-muted-foreground">{t.body}</p>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">Per-lead unlock also available at <strong className="text-asphalt">A$49.99</strong> per lead (incl. GST). Builder-referred jobs: <strong className="text-asphalt">10% commission (excl. GST)</strong> on the invoiced value.</p>
        </div>
      </section>

      <section id="apply" className="py-20">
        <div className="rs-container grid md:grid-cols-3 gap-10">
          <aside className="space-y-6 text-sm md:col-span-1">
            <div className="border-l-4 border-safety pl-4">
              <p className="rs-eyebrow">Who we work with</p>
              <p className="mt-2 text-muted-foreground">Site clean-up crews, waste haulage operators, small excavation teams and end-of-build tidy specialists.</p>
            </div>
            <div className="border-l-4 border-asphalt pl-4">
              <p className="rs-eyebrow">What we ask</p>
              <p className="mt-2 text-muted-foreground">Reliable communication, professional turn-out, proper waste disposal and honest quoting.</p>
            </div>
            <div className="border-l-4 border-safety pl-4">
              <p className="rs-eyebrow">Not a licence</p>
              <p className="mt-2 text-muted-foreground">Joining the network is not a licence or endorsement. Each job is subject to your own assessment and contract with the customer.</p>
            </div>
          </aside>

          {done ? (
            <div className="md:col-span-2 bg-white border border-border p-8 text-center">
              <CheckCircle2 className="mx-auto text-safety" size={48} />
              <h2 className="font-display text-3xl uppercase mt-4">Application received</h2>
              <p className="mt-2 text-muted-foreground max-w-md mx-auto">Thanks — we've got your details. We'll be in touch as leads come in that match your service area and capabilities.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="md:col-span-2 bg-white border border-border p-6 md:p-8 space-y-4" data-testid="contractor-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block"><span className="rs-label">Business name *</span><input data-testid="contractor-business_name" className="rs-input" required value={form.business_name} onChange={set("business_name")} /></label>
                <label className="block"><span className="rs-label">Contact person *</span><input data-testid="contractor-contact_person" className="rs-input" required value={form.contact_person} onChange={set("contact_person")} /></label>
                <label className="block"><span className="rs-label">Phone *</span><input data-testid="contractor-phone" className="rs-input" required value={form.phone} onChange={set("phone")} /></label>
                <label className="block"><span className="rs-label">Email *</span><input data-testid="contractor-email" type="email" className="rs-input" required value={form.email} onChange={set("email")} /></label>
                <label className="block"><span className="rs-label">ABN</span><input data-testid="contractor-abn" className="rs-input" value={form.abn} onChange={set("abn")} /></label>
                <label className="block"><span className="rs-label">Insurance status</span><input data-testid="contractor-insurance_status" className="rs-input" placeholder="e.g. Public liability $20M" value={form.insurance_status} onChange={set("insurance_status")} /></label>
              </div>
              <label className="block"><span className="rs-label">Service areas *</span><input data-testid="contractor-service_areas" className="rs-input" required placeholder="e.g. Northern Melbourne, Epping, Craigieburn" value={form.service_areas} onChange={set("service_areas")} /></label>
              <label className="block"><span className="rs-label">Services offered *</span><textarea data-testid="contractor-services_offered" rows={3} className="rs-input" required placeholder="e.g. Vacant lot clean-up, builder waste haulage, tip runs, small excavation" value={form.services_offered} onChange={set("services_offered")} /></label>
              <label className="block"><span className="rs-label">Waste disposal capability</span><textarea data-testid="contractor-waste_disposal_capability" rows={2} className="rs-input" placeholder="Truck sizes, skip bins, sorted vs mixed loads, tip accounts" value={form.waste_disposal_capability} onChange={set("waste_disposal_capability")} /></label>
              <label className="block"><span className="rs-label">Notes</span><textarea data-testid="contractor-notes" rows={3} className="rs-input" value={form.notes} onChange={set("notes")} /></label>
              <button type="submit" disabled={submitting} className="rs-btn-primary w-full disabled:opacity-70" data-testid="contractor-submit">
                {submitting ? <><Loader2 className="animate-spin mr-2" size={16}/> Sending…</> : "Submit application"}
              </button>
            </form>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
